"use client";

import { useCallback, useEffect, useRef, useState } from "react";

const WIDGET_JS = "https://assets.calendly.com/assets/external/widget.js";
const WIDGET_CSS = "https://assets.calendly.com/assets/external/widget.css";

declare global {
  interface Window {
    Calendly?: {
      initPopupWidget: (options: {
        url: string;
        prefill?: { name?: string; email?: string; customAnswers?: Record<string, string> };
        utm?: Record<string, string>;
      }) => void;
    };
  }
}

/** Module-level so the assets are injected once per page, not once per component instance. */
let assetsPromise: Promise<void> | null = null;


function loadCalendlyAssets(): Promise<void> {
  if (assetsPromise) return assetsPromise;

  assetsPromise = new Promise<void>((resolve, reject) => {
    if (!document.querySelector(`link[href="${WIDGET_CSS}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = WIDGET_CSS;
      document.head.appendChild(link);
    }

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${WIDGET_JS}"]`);
    if (existing) {
      if (window.Calendly) resolve();
      else {
        existing.addEventListener("load", () => resolve(), { once: true });
        existing.addEventListener("error", () => reject(new Error("Calendly failed to load")), {
          once: true,
        });
      }
      return;
    }

    const script = document.createElement("script");
    script.src = WIDGET_JS;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Let a later click retry rather than caching the failure forever.
      assetsPromise = null;
      reject(new Error("Calendly failed to load"));
    };
    document.body.appendChild(script);
  });

  return assetsPromise;
}

const BRAND_VIOLET = "783DD7";
const DARK = { background_color: "0a0a0f", text_color: "f5f5f7" };
const LIGHT = { background_color: "ffffff", text_color: "1a1a1f" };

function themedUrl(url: string) {
  const theme = document.documentElement.dataset.theme === "light" ? LIGHT : DARK;
  const u = new URL(url);
  u.searchParams.set("background_color", theme.background_color);
  u.searchParams.set("text_color", theme.text_color);
  u.searchParams.set("primary_color", BRAND_VIOLET);
  u.searchParams.set("hide_gdpr_banner", "1");
  return u.toString();
}

/** Loads the widget on first click, keeping it off the contact page's critical path. */
export function useCalendlyPopup(url: string | undefined) {
  const [loading, setLoading] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const open = useCallback(async () => {
    if (!url) return;
    setLoading(true);
    try {
      await loadCalendlyAssets();
      window.Calendly?.initPopupWidget({ url: themedUrl(url) });
    } catch {
      // Widget blocked (ad-blocker, offline, CSP).
      window.open(url, "_blank", "noopener,noreferrer");
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [url]);

  return { open, loading };
}
